import { useMemo } from 'react'
import { useSetting } from './useSetting'
import { useTransactions } from './useTransactions'
import { useAppSymbols } from './useAppSymbols'
import { aggregateDailyPL, localDateKey } from '../lib/pl'

export const DAILY_CLOSE_LIMIT_KEY = 'max-closed-positions-per-day'

export interface DailyCloseLimitStatus {
  /** Positions fully closed today (round-trip cycles, scoped to app-placed symbols). */
  todayClosedCount: number
  /** User-configured cap, or `null` when the limit is off. */
  cap: number | null
  /** True when a cap is set and today's count meets or exceeds it. */
  capReached: boolean
  /** Short clause for tooltips/banners, e.g. "Daily limit reached (5 / 5 closed positions today)". Null when not capped. */
  capReason: string | null
}

/**
 * Reads the daily closed-position cap from settings and reports whether the
 * user has hit it. Counter source is the same `aggregateDailyPL` that powers
 * the /trades day-strip and /calendar, so this stays consistent with what the
 * UI shows.
 */
export function useDailyCloseLimit(): DailyCloseLimitStatus {
  const [cap] = useSetting<number | null>(DAILY_CLOSE_LIMIT_KEY, null)
  const txQuery = useTransactions()
  const { appSymbols } = useAppSymbols()

  const todayClosedCount = useMemo(() => {
    const daily = aggregateDailyPL(txQuery.data ?? [], { symbolFilter: appSymbols })
    const todayKey = localDateKey(new Date().toISOString())
    return daily.get(todayKey)?.tradeCount ?? 0
  }, [txQuery.data, appSymbols])

  const capReached = cap != null && cap > 0 && todayClosedCount >= cap
  const capReason = capReached
    ? `Daily limit reached (${todayClosedCount} / ${cap} closed positions today)`
    : null
  return { todayClosedCount, cap, capReached, capReason }
}
